/**
 * Página de estadísticas de auditoría
 */

import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useState } from 'react';
import {
  Assessment,
  People,
  Category,
  TrendingUp,
  Timeline,
} from '@mui/icons-material';
import { useFetchAuditoriaEstadisticas } from '@/shared/actions/erp/auditoria.actions';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D'];

export const AuditoriaEstadisticasPage = () => {
  const [dias, setDias] = useState<number>(30);
  const { data: stats, isLoading, error } = useFetchAuditoriaEstadisticas(dias);

  const handleDiasChange = (event: SelectChangeEvent<number>) => {
    setDias(Number(event.target.value));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error al cargar estadísticas: {error instanceof Error ? error.message : 'Error desconocido'}
      </Alert>
    );
  }

  if (!stats) {
    return <Alert severity="info">No hay datos de estadísticas disponibles</Alert>;
  }

  // Preparar datos para gráficos
  const modulosData = stats.por_modulo.map((item) => ({
    name: item.modulo,
    total: item.total,
  }));

  const accionesData = stats.por_accion.map((item) => ({
    name: item.accion,
    total: item.total,
  }));

  const usuariosData = stats.por_usuario.slice(0, 10).map((item) => ({
    name: item.usuario__username || 'Sin nombre',
    nombre: `${item.usuario__first_name || ''} ${item.usuario__last_name || ''}`.trim() || item.usuario__username,
    total: item.total,
  }));

  return (
    <Box>
      {/* Header con selector de período */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="medium">
          <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
          Estadísticas de Auditoría
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Período</InputLabel>
          <Select value={dias} onChange={handleDiasChange} label="Período">
            <MenuItem value={7}>Últimos 7 días</MenuItem>
            <MenuItem value={15}>Últimos 15 días</MenuItem>
            <MenuItem value={30}>Últimos 30 días</MenuItem>
            <MenuItem value={60}>Últimos 60 días</MenuItem>
            <MenuItem value={90}>Últimos 90 días</MenuItem>
            <MenuItem value={180}>Últimos 6 meses</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* KPIs principales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total de Logs
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total_logs.toLocaleString()}
                  </Typography>
                </Box>
                <Timeline color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" mt={1}>
                Últimos {stats.periodo_dias} días
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Módulos Activos
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.por_modulo.length}
                  </Typography>
                </Box>
                <Category color="success" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" mt={1}>
                Con actividad registrada
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Usuarios Activos
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.por_usuario.length}
                  </Typography>
                </Box>
                <People color="info" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" mt={1}>
                Con acciones auditadas
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Tipos de Acciones
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.por_accion.length}
                  </Typography>
                </Box>
                <TrendingUp color="warning" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" mt={1}>
                Diferentes acciones
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3}>
        {/* Logs por Módulo */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Logs por Módulo
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={modulosData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#8884d8" name="Total de logs" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Logs por Acción */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Logs por Acción
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={accionesData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry: any) => `${entry.name}: ${entry.total}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="total"
                >
                  {accionesData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top 10 Usuarios más Activos */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Usuarios Más Activos
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usuariosData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="nombre" type="category" width={150} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#82ca9d" name="Total de acciones" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Detalles por Módulo */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detalles por Módulo
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {stats.por_modulo.map((modulo, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={1}
                  borderBottom="1px solid"
                  borderColor="divider"
                >
                  <Typography variant="body2">{modulo.modulo}</Typography>
                  <Chip label={modulo.total.toLocaleString()} color="primary" size="small" />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Detalles por Acción */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detalles por Acción
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {stats.por_accion.map((accion, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={1}
                  borderBottom="1px solid"
                  borderColor="divider"
                >
                  <Typography variant="body2">{accion.accion}</Typography>
                  <Chip label={accion.total.toLocaleString()} color="primary" size="small" />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
